import { pieceOf } from "@eldrun/content";
import { ConeGeometry, Mesh, type BufferGeometry, type Material, type Object3D, type Scene, type ShaderMaterial } from "three";
import { InstanceBatcher } from "../render/instance-batcher.ts";
import { models, type ModelLibrary } from "../render/model-library.ts";
import { QUALITY } from "../render/quality.ts";
import { LID_OPEN_ANGLE, structureGeometry, structureLid, structureModeled } from "../render/structure-meshes.ts";
import { unindexed } from "../render/geometry/unindexed.ts";
import { isTreasureChest, pulseTreasure } from "../render/treasure.ts";
import type { DissolvePart } from "./dissolves.ts";

/**
 * Distância de desenho (m, vezes a distância de visão das Opções; F36): baús, estações, cruzes e
 * trouxas somem além de 160 m (a neblina e o tamanho já os escondem); peças da grade, que formam casas
 * vistas de longe, além de 400 m. Cada estrutura custa um draw call ou mais.
 */
export const STRUCTURE_DRAW_RADIUS = { small: 160, piece: 400 } as const;

/** Além desta distância (m, vezes a distância de visão), as peças da grade com modelo usam o LOD de longe (F48). */
export const STRUCTURE_LOD_DISTANCE = 120;
/**
 * Histerese da troca de LOD: a peça que foi para o de longe só volta abaixo de 95 % da distância (114 m
 * no Médio), para não pular entre os dois a cada quadro na borda.
 */
export const STRUCTURE_LOD_RETURN = 0.95;

/** Raio de desenho de uma estrutura pelo item (peça da grade ou não). */
export const structureDrawRadius = (itemId: string): number => (pieceOf(itemId) ? STRUCTURE_DRAW_RADIUS.piece : STRUCTURE_DRAW_RADIUS.small);

/**
 * Onde a chama fica em cada estação acesa (no espaço da estação) e o tamanho dela: o Forno solta o fogo
 * pela boca (+z, a frente), não de dentro da cúpula.
 */
export const FLAME_ANCHORS: Readonly<Record<string, { readonly position: readonly [number, number, number]; readonly scale: number }>> = {
  "forno-barro": { position: [0, 0.45, 0.62], scale: 0.65 },
};
/** Chama de uma estação sem lugar próprio. */
const DEFAULT_FLAME = { position: [0, 0.35, -0.42], scale: 1 } as const;

export interface StructureLike {
  itemId: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Porta aberta, Baú de Simpatia já comprado. */
  open?: boolean;
}

export interface StructureLayerOptions {
  /** Modelos (F48); sem eles, as malhas procedurais. Padrão: a biblioteca carregada da página. */
  library?: ModelLibrary;
  /**
   * Material dos baús de saque ainda não abertos (Fase 15, `createTreasureMaterial`): brilham e pulsam
   * até este jogador abrir (ou o Baú de Simpatia ser comprado). Sem ele, todos no material da camada.
   */
  treasure?: Material;
}

export interface StructureLayerStats {
  /** Estruturas na camada. */
  total: number;
  /** Desenhadas no último quadro. */
  drawn: number;
  /** Com modelo do pacote `estruturas` (F48) e com a malha procedural. */
  models: number;
  procedural: number;
  /** Tampas à parte, e quantas estão abertas (compradas, ou o baú aberto por este jogador). */
  lids: number;
  open: number;
  /** Baús de saque brilhando (ainda não abertos). */
  treasures: number;
  /** Peças da grade desenhadas com o LOD de longe no último quadro. */
  far: number;
  /** Lotes desenhados no último quadro (= draw calls). */
  drawCalls: number;
}

/** O que a camada guarda de cada estrutura. */
interface Entry {
  mesh: Mesh;
  /** Raio de desenho (m, antes do fator de qualidade). */
  radius: number;
  near: BufferGeometry;
  /** Malha de longe (peças da grade com modelo); sem ela, a de perto sempre. */
  far?: BufferGeometry;
  lid?: Object3D;
  modeled: boolean;
  /** Tampa aberta pelo estado (Baú de Simpatia comprado) e por quem abriu o baú aqui (só local). */
  open: boolean;
  localOpen: boolean;
  /** Desenhada com o LOD de longe (com histerese). */
  isFar: boolean;
  /** Baú de saque (Fase 15): brilha enquanto não foi aberto. */
  treasure: boolean;
  /** Já aberto por este jogador alguma vez (o brilho apaga e não volta). */
  seen: boolean;
}

/**
 * Estruturas posicionadas e do mundo (baús, mascates, peças da grade): uma malha por estrutura com
 * geometria compartilhada por tipo, desenhada só até o raio do tipo e em lotes (F36: os baús da ilha
 * inteira custavam ~110 draw calls de qualquer ponto; uma base de 400 peças custaria 400). As malhas
 * ficam fora da cena, só para as transformações (porta, tampa, chama); o lote desenha. Com os modelos
 * (F48), bancada, bigorna, piso, alicerce e baús vêm do pacote `estruturas`; a tampa dos baús gira na
 * dobradiça, e as peças da grade trocam para o LOD de longe além de `STRUCTURE_LOD_DISTANCE`.
 */
export class StructureLayer {
  private readonly batcher: InstanceBatcher;
  private readonly material: Material;
  private readonly treasure: Material | undefined;
  private readonly library: ModelLibrary;
  private readonly entries = new Map<string, Entry>();
  private drawn = 0;
  private far = 0;
  /** Chamas das estações acesas (Forno, Panela), com material de brilho próprio. */
  private readonly flames = new Map<string, { mesh: Mesh; scale: number }>();
  private readonly flameMaterial: Material | undefined;
  private readonly flameGeometry = unindexed(new ConeGeometry(0.18, 0.45, 6));

  constructor(scene: Scene, material: Material, flameMaterial?: Material, options: StructureLayerOptions = {}) {
    this.batcher = new InstanceBatcher(scene, "estruturas");
    this.material = material;
    this.flameMaterial = flameMaterial;
    this.treasure = options.treasure;
    this.library = options.library ?? models.library;
  }

  /** Acende ou apaga a chama da estação (o fogo é público; o conteúdo, não). */
  setLit(id: string, lit: boolean): void {
    const entry = this.entries.get(id);
    const flame = this.flames.get(id);
    if (!lit || !entry || !this.flameMaterial) {
      if (flame) {
        flame.mesh.removeFromParent();
        this.flames.delete(id);
      }
      return;
    }
    if (flame) return;
    const anchor = FLAME_ANCHORS[entry.mesh.userData["itemId"] as string] ?? DEFAULT_FLAME;
    const f = new Mesh(this.flameGeometry, this.flameMaterial);
    f.position.fromArray(anchor.position);
    entry.mesh.add(f);
    this.flames.set(id, { mesh: f, scale: anchor.scale });
  }

  /** Chamas tremulando (`now` em s), o corte por distância da câmera e o LOD das peças da grade. */
  update(now: number, cameraX: number, cameraZ: number): void {
    // Chamas primeiro: o lote copia as matrizes do quadro.
    for (const [id, { mesh, scale }] of this.flames) {
      const k = Number(id) * 1.7;
      mesh.scale.set(scale, scale * (0.8 + 0.3 * Math.abs(Math.sin(now * 9 + k)) + 0.1 * Math.sin(now * 23 + k)), scale);
    }
    if (this.treasure && (this.treasure as ShaderMaterial).isShaderMaterial) pulseTreasure(this.treasure as ShaderMaterial, now, QUALITY.post.reduceFlashes);
    this.drawn = 0;
    this.far = 0;
    const lod = STRUCTURE_LOD_DISTANCE * QUALITY.distance;
    this.batcher.begin();
    for (const entry of this.entries.values()) {
      const mesh = entry.mesh;
      const r = entry.radius * QUALITY.distance;
      const d2 = (mesh.position.x - cameraX) ** 2 + (mesh.position.z - cameraZ) ** 2;
      mesh.visible = d2 <= r * r;
      if (!mesh.visible) continue;
      if (entry.far) {
        const edge = entry.isFar ? lod * STRUCTURE_LOD_RETURN : lod;
        entry.isFar = d2 > edge * edge;
        mesh.geometry = entry.isFar ? entry.far : entry.near;
        if (entry.isFar) this.far++;
      }
      this.drawn++;
      this.batcher.addTree(mesh);
    }
    this.batcher.end();
  }

  get lit(): number {
    return this.flames.size;
  }

  get size(): number {
    return this.entries.size;
  }

  /** Estruturas desenhadas no último quadro. */
  get visible(): number {
    return this.drawn;
  }

  stats(): StructureLayerStats {
    let modeled = 0;
    let lids = 0;
    let open = 0;
    let treasures = 0;
    for (const e of this.entries.values()) {
      if (e.modeled) modeled++;
      if (e.lid) lids++;
      if (e.lid && (e.open || e.localOpen)) open++;
      if (this.treasure && e.mesh.material === this.treasure) treasures++;
    }
    return { total: this.entries.size, drawn: this.drawn, models: modeled, procedural: this.entries.size - modeled, lids, open, treasures, far: this.far, drawCalls: this.batcher.count };
  }

  /**
   * Estrutura nova, ou a mesma de novo (o estado a recriou): a mesma estrutura no mesmo id mantém a
   * tampa aberta aqui e a chama.
   */
  add(id: string, s: StructureLike): void {
    const previous = this.entries.get(id);
    const same = previous?.mesh.userData["itemId"] === s.itemId;
    const kept = { localOpen: same && previous!.localOpen, seen: same && previous!.seen, lit: same && this.flames.has(id) };
    this.remove(id);
    const near = structureGeometry(s.itemId, this.library);
    const far = structureGeometry(s.itemId, this.library, 1);
    const mesh = new Mesh(near, this.material);
    mesh.position.set(s.x, s.y, s.z);
    mesh.rotation.y = s.yaw;
    mesh.name = `estrutura:${id}`;
    mesh.userData["itemId"] = s.itemId;
    // Porta: a folha gira na dobradiça (lado −x do vão de 1 m).
    if (s.itemId === "parede-porta") {
      const leaf = new Mesh(structureGeometry("parede-porta:folha", this.library), this.material);
      leaf.position.set(-0.5, 0, 0);
      leaf.name = "folha";
      mesh.add(leaf);
    }
    // Baú com tampa à parte: gira na dobradiça (Baú de Simpatia comprado, ou o baú aberto aqui).
    const lidModel = structureLid(s.itemId, this.library);
    const lid = lidModel && new Mesh(lidModel.geometry, this.material);
    if (lid && lidModel) {
      lid.name = "tampa";
      lid.position.fromArray(lidModel.hinge);
      mesh.add(lid);
    }
    const entry: Entry = {
      mesh,
      radius: structureDrawRadius(s.itemId),
      near,
      ...(far !== near && { far }),
      ...(lid && { lid }),
      modeled: structureModeled(s.itemId, this.library),
      open: s.open === true,
      localOpen: kept.localOpen,
      isFar: false,
      treasure: isTreasureChest(s.itemId),
      seen: kept.seen || kept.localOpen,
    };
    this.entries.set(id, entry);
    placeLid(entry);
    this.paint(entry);
    if (kept.lit) this.setLit(id, true);
  }

  /** Malha (fora da cena) da estrutura, com a folha, a tampa e a chama como filhas: para testes. */
  objectOf(id: string): Object3D | undefined {
    return this.entries.get(id)?.mesh;
  }

  /**
   * As peças da estrutura para o dissolve (F95): a malha e as filhas no material da camada (a folha da
   * porta, a tampa; a chama fica de fora), na matriz do mundo do último quadro. Vazio se ela não foi
   * desenhada no último quadro (além do raio) ou se o material não é o de superfície. Quem chama tira a
   * estrutura (`remove`) em seguida: o lote do próximo quadro já sai sem ela.
   */
  dissolveParts(id: string): DissolvePart[] {
    const entry = this.entries.get(id);
    const material = this.material as ShaderMaterial;
    if (!entry || !entry.mesh.visible || material.isShaderMaterial !== true) return [];
    entry.mesh.updateMatrixWorld(true);
    const out: DissolvePart[] = [];
    entry.mesh.traverseVisible((o) => {
      const part = o as Mesh;
      if (part.isMesh && (part.material === material || part.material === this.treasure)) out.push({ geometry: part.geometry, matrix: part.matrixWorld, material: part.material as ShaderMaterial });
    });
    return out;
  }

  /** A estação desceu (a peça embaixo dela saiu). */
  setY(id: string, y: number): void {
    const entry = this.entries.get(id);
    if (entry) entry.mesh.position.y = y;
  }

  /** Porta aberta: a folha gira 90° para dentro da célula de trás; Baú de Simpatia: a tampa levanta. */
  setOpen(id: string, open: boolean): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    const leaf = entry.mesh.getObjectByName("folha");
    if (leaf) leaf.rotation.y = open ? Math.PI / 2 : 0;
    entry.open = open;
    placeLid(entry);
    this.paint(entry);
  }

  /**
   * Baú aberto por este jogador (F48): a tampa levanta só aqui, enquanto o painel dele está aberto. O
   * servidor não publica o baú aberto: os outros o veem fechado.
   */
  setLocalOpen(id: string, open: boolean): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.localOpen = open;
    if (open) entry.seen = true;
    placeLid(entry);
    this.paint(entry);
  }

  /** Corpo e tampa no material do tesouro enquanto o baú de saque não foi aberto; depois, no da camada. */
  private paint(entry: Entry): void {
    const glowing = this.treasure !== undefined && entry.treasure && !entry.seen && !entry.open;
    const material = glowing ? this.treasure! : this.material;
    entry.mesh.material = material;
    if (entry.lid) (entry.lid as Mesh).material = material;
  }

  remove(id: string): void {
    if (!this.entries.has(id)) return;
    this.setLit(id, false);
    this.entries.delete(id);
  }

  dispose(): void {
    for (const id of [...this.entries.keys()]) this.remove(id);
    this.batcher.dispose();
    this.flameGeometry.dispose();
  }
}

/** Tampa fechada sobre o corpo, ou girada para trás na dobradiça (a borda de trás do topo). */
function placeLid(entry: Entry): void {
  if (entry.lid) entry.lid.rotation.x = entry.open || entry.localOpen ? -LID_OPEN_ANGLE : 0;
}
